import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import GlobalDialogContent from "../../GlobalDialogContent";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { useDispatch, useSelector } from "react-redux";
import { SewaCategoryListService, SewaStoreService, SewaUpdateService } from "../../../../utils/services/sewa-master.service";
import { showSnackbar } from "../../../../redux/reducer/snackbarSlice";
import { closeDialog } from "../../../../redux/reducer/dialogSlice";
import { setModuleRefresh } from "../../../../redux/reducer/registration/RefreshSlice";
import { Send } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const AddSewa = () => {
    const payload = useSelector((state: any) => state.dialog?.payload);
    const dispatch = useDispatch();
    const [sewaList, setSewaList] = useState<any[]>([])
    const [removedDocs, setRemovedDocs] = useState<number[]>([]);

    const formik = useFormik({
        initialValues: {
            category_name: payload?.category_name || '',
            sewa_category_id: payload?.sewa_category_id || '',
            sewa_name: payload?.sewa_name || '',
            address_or_location: payload?.address_or_location || '',
            description: payload?.description || '',
            sewa_date: payload?.sewa_date || '',
            photos: payload?.documents?.filter((d: any) => d.document_type === "sewa_photo") || [],
            documents: payload?.documents?.filter((d: any) => d.document_type === "additional_document") || [],

        },
        validationSchema: Yup.object({
            sewa_category_id: Yup.string().required('Category is Required'),
            sewa_name: Yup.string().required('Sewa Name is Required'),
            address_or_location: Yup.string().required('Address is Required'),
            description: Yup.string().required('Description is Required'),
            sewa_date: Yup.string().required('Date is Required'),
            photos: Yup.array()
                .min(1, 'At least 1 photo is required')
                .max(5, 'Maximum 5 photos allowed'),

            documents: Yup.array()
                .min(1, 'At least 1 document is required')
                .max(5, 'Maximum 5 documents allowed'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            const formData = new FormData();
            formData.append("sewa_category_id", values.sewa_category_id);
            formData.append("sewa_name", values.sewa_name);
            formData.append("address_or_location", values.address_or_location);
            formData.append("description", values.description);
            formData.append("sewa_date", dayjs(values.sewa_date).format('YYYY-MM-DD'));

            values.photos.forEach((file: any) => {
                formData.append("photos", file);
            });

            values.documents.forEach((file: any) => {
                formData.append("documents", file);
            });

            if (payload?.id) {
                formData.append("id", payload.id);
            }

            formData.append("deleted_document_ids", JSON.stringify(removedDocs));

            const apiFunction = payload?.id ? SewaUpdateService : SewaStoreService;
            const { code, message } = await apiFunction(formData);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || 'Form Submitted Successfully.' }));
                dispatch(closeDialog());
                dispatch(setModuleRefresh({ moduleName: 'SewaMasterRefresh', refresh: true }));
            } else {
                dispatch(showSnackbar({ type: "error", message: message || 'Something Went Wrong!' }));
            }
            setSubmitting(false);
        }
    });

    useEffect(() => {
        fetchSewaCategory();
    }, [])

    const fetchSewaCategory = async () => {
        const { code, data } = await SewaCategoryListService({});
        if (code === 200 && data?.sewa_categories) {
            setSewaList(data?.sewa_categories);
        } else {
            setSewaList([]);
        }
    }
    const handlePhotoChange = (e: any) => {
        const files = Array.from(e.target.files);
        const updated = [...formik.values.photos, ...files].slice(0, 5);
        formik.setFieldValue("photos", updated);
    };

    const handleDocumentChange = (e: any) => {
        const files = Array.from(e.target.files);
        const allowedTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",];
        const validFiles = files.filter((file: any) =>
            allowedTypes.includes(file.type)
        );
        const updated = [...formik.values.documents, ...validFiles].slice(0, 5);
        formik.setFieldValue("documents", updated);
    };

    const handleRemovePhoto = (index: number) => {
        const file = formik.values.photos[index];

        if (!(file instanceof File)) {
            setRemovedDocs((prev) => [...prev, file.document_id]);
        }

        const updated = [...formik.values.photos];
        updated.splice(index, 1);
        formik.setFieldValue("photos", updated);
    };


    const handleRemoveDocument = (index: number) => {
        const file = formik.values.documents[index];

        if (!(file instanceof File)) {
            setRemovedDocs((prev) => [...prev, file.document_id]);
        }

        const updated = [...formik.values.documents];
        updated.splice(index, 1);
        formik.setFieldValue("documents", updated);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <GlobalDialogContent
                dialogBody={
                    <Grid container spacing={2}>
                        <Grid item xs={4} >
                            <Autocomplete
                                options={sewaList}
                                getOptionLabel={(option) => option?.category_name || ""}
                                value={sewaList.find(
                                    (item) => item.id === formik.values.sewa_category_id
                                ) || null}
                                onChange={(e, value) => {
                                    formik.setFieldValue('sewa_category_id', value?.id || '');
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disableClearable
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Select Sewa Dal *"
                                    />
                                )}
                            />

                        </Grid>
                        <Grid item xs={4} >
                            <TextField
                                fullWidth
                                label="Name of Sewa *"
                                name="sewa_name"
                                size="small"
                                value={formik.values.sewa_name}
                                onChange={formik.handleChange}
                                placeholder="Enter Sewa Name"
                                error={formik.touched.sewa_name && Boolean(formik.errors.sewa_name)}
                                helperText={formik.touched.sewa_name && Boolean(formik.errors.sewa_name)}
                            />
                        </Grid>
                        <Grid item xs={4} >
                            <DatePicker
                                value={formik.values.sewa_date ? dayjs(formik.values.sewa_date) : null}
                                onChange={(value) =>
                                    formik.setFieldValue("sewa_date", value ? value.toDate() : null)
                                }
                                sx={{ ".MuiButtonBase-root": { marginRight: 0 } }}
                                format="DD-MM-YYYY"
                                minDate={dayjs()}
                                slotProps={{
                                    textField: {
                                        label: 'Sewa Date',
                                        placeholder: 'Select Sewa Date',
                                        fullWidth: true,
                                        size: "small",
                                        inputProps: { style: { textOverflow: "ellipsis" } },
                                        error: formik.touched.sewa_date && Boolean(formik.errors.sewa_date),
                                        helperText:
                                            formik.touched.sewa_date && typeof formik.errors.sewa_date === "string"
                                                ? formik.errors.sewa_date
                                                : "",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address or Location*"
                                name="address_or_location"
                                size="small"
                                value={formik.values.address_or_location}
                                onChange={formik.handleChange}
                                placeholder="Enter Address"
                                error={formik.touched.address_or_location && Boolean(formik.errors.address_or_location)}
                                helperText={formik.touched.address_or_location && Boolean(formik.errors.address_or_location)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description*"
                                name="description"
                                size="small"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                placeholder="Enter Description"
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description && Boolean(formik.errors.description)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <label>Photos *</label>
                            <Grid container spacing={1} mt={1}>
                                {formik.values.photos?.map((file: any, index: number) => (
                                    <Grid item key={index}>
                                        <div style={{ position: "relative", border: '1px solid #ffcdcd', borderRadius: '6px' }}>
                                            <img
                                                src={
                                                    file instanceof File
                                                        ? URL.createObjectURL(file)
                                                        : `${process.env.REACT_APP_CDN_URL}/${file.document_path}`
                                                }
                                                alt="photo"
                                                width={60}
                                                height={60}
                                                style={{ borderRadius: 6 }}
                                            />

                                            <HighlightOffIcon onClick={() => handleRemovePhoto(index)} style={{ position: "absolute", top: -8, right: -8, cursor: "pointer", borderRadius: "50%", fontSize: 18 }} color="error" />
                                        </div>
                                    </Grid>
                                ))}

                                {formik.values.photos?.length < 5 && (
                                    <Grid item>
                                        <Button variant="text" component="label">
                                            <AddCircleOutlineIcon />
                                            <input
                                                type="file"
                                                hidden
                                                multiple
                                                onChange={handlePhotoChange}
                                                accept="image/*"
                                            />
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <label>Documents *</label>
                            <Grid container spacing={1} mt={1}>
                                {formik.values.documents?.map((file: any, index: number) => (
                                    <Grid item key={index}>
                                        <div style={{ position: "relative", border: '1px solid #ffcdcd', borderRadius: '6px' }}>
                                            <Button variant="text">
                                                <TextSnippetIcon sx={{ mr: 1 }} />
                                                {file instanceof File
                                                    ? file.name
                                                    : file.document_path.split("/").pop()}
                                            </Button>

                                            <HighlightOffIcon onClick={() => handleRemoveDocument(index)} style={{ position: "absolute", top: -8, right: -8, cursor: "pointer", borderRadius: "50%", fontSize: 18 }} color="error" />
                                        </div>
                                    </Grid>
                                ))}

                                {formik.values.documents?.length < 5 && (
                                    <Grid item>
                                        <Button variant="text" component="label">
                                            <AddCircleOutlineIcon />
                                            <input
                                                type="file"
                                                hidden
                                                multiple
                                                onChange={handleDocumentChange}
                                                accept=".pdf,.xlsx"
                                            />
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>


                    </Grid>
                }
                dialogFooter={
                    <Button type="submit" disabled={formik.isSubmitting || !formik.isValid} variant="contained" color="primary" startIcon={<Send />} >
                        {formik.isSubmitting ? payload?.id ? "Updating..." : "Adding..." : payload?.id ? "Update Sewa Dal" : "Add Sewa Dal"}
                    </Button>
                }
            />
        </form>
    )
}

export default AddSewa;